import { useEffect, useState } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Eye, ShoppingBag, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import ImportExportButtons from "@/components/ImportExportButtons";

const STATUS_FLOW = ["order_confirmed","in_production","quality_check","packaging_in_progress","packed","ready_for_dispatch","shipped","in_transit","out_for_delivery","delivered"];

export default function SalesOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set("q", filter);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/sales/orders?${params.toString()}`);
      setOrders(data);
    } catch { toast.error("Failed to load orders"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, statusFilter]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/sales/orders/${id}/status`, { status }); toast.success("Status updated"); load(); if (selected) setSelected({ ...selected, status }); }
    catch { toast.error("Update failed"); }
  };

  const deleteOrder = async (order) => {
    try {
      const { data: deps } = await api.get(`/sales/orders/${order._id}/dependencies`);
      const detail = Object.entries(deps.counts).filter(([, v]) => v > 0).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join("\n");
      const isSuper = user?.role === "super_admin";
      if (deps.hard_lock) {
        if (!isSuper) {
          window.alert(`Cannot delete ${order.order_number}.\n\nLinked records:\n${detail}\n\n⛔ This order has invoice or confirmed payment — deletion is blocked. Cancel it instead, or ask a super_admin to force-delete.`);
          return;
        }
        const msg =
          `⚠️  FORCE DELETE ${order.order_number}?\n\n` +
          `This order has:\n${detail}\n\n` +
          `Force-delete will:\n` +
          `  • Delete the order & any linked invoice/challan + their attachments\n` +
          `  • Cascade delete linked packages, QR scans, inventory transactions, exchanges\n` +
          `  • Release reserved inventory back to stock\n` +
          `  • Ignore the invoice/payment safety check\n\n` +
          `This is IRREVERSIBLE. Continue?`;
        if (!window.confirm(msg)) return;
        const { data } = await api.delete(`/sales/orders/${order._id}?force=true`);
        toast.success(`Force-deleted · released ${data.cascade?.reserved_released || 0} reserved units`);
      } else {
        const msg = `Delete order ${order.order_number}?\n\n` +
          (deps.total > 0 ? `Linked records that will be affected:\n${detail}\n\n` : "No linked records.\n\n") +
          "This action is irreversible.";
        if (!window.confirm(msg)) return;
        await api.delete(`/sales/orders/${order._id}`);
        toast.success("Order deleted");
      }
      setSelected(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="sales-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Sales orders" description="Website, wholesale, exhibition and offline orders — one command centre with full history." tint="terracotta" icon={ShoppingBag} actions={<ImportExportButtons entity="sales_orders" canImport={false}/>} />
      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={filter} onChange={(e)=>setFilter(e.target.value)} placeholder="Search order, customer, AWB…"
                 className="input-soft pl-9" data-testid="sales-search"/>
        </div>
        <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}
                className="input-soft max-w-[220px]" data-testid="sales-status-filter">
          <option value="">All statuses</option>
          {STATUS_FLOW.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
          <option value="cancelled">cancelled</option>
          <option value="exchange_requested">exchange requested</option>
        </select>
      </div>

      <div className="card-soft p-0 overflow-x-auto">
        <table className="table-modern">
          <thead><tr>
            <th>Order</th><th>Customer</th><th>Source</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th>Placed</th><th></th>
          </tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o._id} data-testid={`sales-row-${o.order_number}`}>
                <td className="font-mono text-xs">{o.order_number}</td>
                <td>
                  <div className="font-medium">{o.customer?.name}</div>
                  <div className="text-xs text-muted-foreground">{o.customer?.phone}</div>
                </td>
                <td className="text-sm text-muted-foreground capitalize">{o.source}</td>
                <td className="text-sm tabular">{o.items?.length || 0}</td>
                <td className="tabular font-medium">{inr(o.total)}</td>
                <td><StatusChip value={o.payment_status}/></td>
                <td><StatusChip value={o.status}/></td>
                <td className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={()=>setSelected(o)} className="btn-ghost btn-sm" data-testid={`sales-view-${o.order_number}`}>
                      <Eye size={14}/> View
                    </button>
                    <RecordActions entity="sales_orders" record={o} frozen={["order_number"]} canDelete={false} onChanged={load} />
                    <button onClick={(e)=>{e.stopPropagation(); deleteOrder(o);}} className="btn-ghost btn-icon text-destructive" data-testid={`sales-delete-${o.order_number}`} title="Delete order">
                      <Trash size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!orders.length && <tr><td colSpan={9}><EmptyState icon={ShoppingBag} title="No orders yet" description="Confirmed orders from your website, wholesale and offline sources will appear here." /></td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 modal-backdrop flex fade-in" onClick={()=>setSelected(null)}>
          <div className="ml-auto w-full max-w-xl h-full bg-card border-l border-border overflow-y-auto drawer-slide" onClick={(e)=>e.stopPropagation()} data-testid="sales-drawer">
            <div className="p-7 border-b border-border sticky top-0 bg-card z-10">
              <div className="label-eyebrow">Sales order</div>
              <div className="display-2 mt-2">{selected.order_number}</div>
              <div className="flex gap-2 mt-4"><StatusChip value={selected.status}/><StatusChip value={selected.payment_status}/></div>
              <div className="flex gap-2 mt-3">
                <button onClick={()=>deleteOrder(selected)} className="btn-ghost btn-sm text-destructive" data-testid="sales-drawer-delete"><Trash size={12}/> Delete order</button>
              </div>
            </div>
            <div className="p-7 space-y-8">
              <div>
                <div className="label-eyebrow mb-2">Customer</div>
                <div className="font-medium">{selected.customer?.name}</div>
                <div className="text-sm text-muted-foreground">{selected.customer?.email} · {selected.customer?.phone}</div>
                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  {selected.shipping_address?.line1}, {selected.shipping_address?.city}, {selected.shipping_address?.state} {selected.shipping_address?.pin}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-3">Items</div>
                <div className="card-soft p-0 overflow-x-auto">
                  <table className="table-modern text-sm">
                    <thead><tr><th>SKU</th><th>Size</th><th>Qty</th><th>Amount</th></tr></thead>
                    <tbody>
                      {selected.items?.map((i,idx)=>(
                        <tr key={idx}>
                          <td className="font-mono text-xs">{i.sku}</td>
                          <td>{i.size} {i.colour}</td>
                          <td className="tabular">{i.quantity}</td>
                          <td className="tabular">{inr(i.total || i.unit_price * i.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular">{inr(selected.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="tabular">{inr(selected.tax_total)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="tabular">{inr(selected.shipping_charge)}</span></div>
                  <div className="flex justify-between font-display text-lg font-semibold pt-3 border-t border-border/70 mt-2"><span>Total</span><span className="tabular">{inr(selected.total)}</span></div>
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-3">Advance status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_FLOW.map((s) => (
                    <button key={s} onClick={()=>setStatus(selected._id, s)} data-testid={`sales-set-${s}`}
                            className={`pill-toggle ${s === selected.status ? "is-active" : ""}`}>{s.replace(/_/g," ")}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-3">History</div>
                <ol className="space-y-2 text-sm border-l border-border pl-4">
                  {selected.status_history?.map((h,idx)=>(
                    <li key={idx} className="flex items-center gap-3"><StatusChip value={h.status}/> <span className="text-xs text-muted-foreground">{fmtDate(h.at)} · {h.by}</span></li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
